import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Search, Home, Users, Calendar, ChevronRight, AlertCircle, Clock } from 'lucide-react';
import { estimateWorkoutSeconds, formatDuration } from '../../utils/workoutDuration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WEEKDAYS = [
  { value: 0, label: 'Lunes' },
  { value: 1, label: 'Martes' },
  { value: 2, label: 'Miércoles' },
  { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' },
  { value: 5, label: 'Sábado' },
  { value: 6, label: 'Domingo' },
];

const SKIP_REASON_LABEL = {
  client_not_found: 'cliente no encontrado',
  not_a_client: 'usuario no es cliente',
  no_target_specified: 'sin destino indicado',
  no_matching_booking: 'sin sesión disponible ese día',
  specific_booking_not_found: 'sesión específica no encontrada',
  specific_booking_wrong_client: 'la sesión no pertenece al cliente',
  specific_booking_already_has_workout: 'la sesión ya tiene rutina',
};

/**
 * BulkAssignTemplateModal — assign one template to many clients in a single action.
 *
 * Flow:
 *   Step 1 — Pick clients (multi-select, search)
 *   Step 2 — Pick a mode for ALL: 'session' (calendar) or 'flexible' (Rutina libre)
 *   Step 3 — Common config:
 *      session  → weekday shortcut + from_date
 *      flexible → optional date range + notes
 *   Confirm → POST, then show created/skipped summary
 */
const BulkAssignTemplateModal = ({ template, onClose, onCompleted }) => {
  // STEP 1: clients
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // STEP 2: mode
  const [mode, setMode] = useState('session');

  // STEP 3 — session
  const today = new Date().toISOString().slice(0, 10);
  const [weekday, setWeekday] = useState(new Date().getDay() === 0 ? 6 : new Date().getDay() - 1); // Mon-based
  const [fromDate, setFromDate] = useState(today);

  // STEP 3 — flexible
  const [availFrom, setAvailFrom] = useState('');
  const [availUntil, setAvailUntil] = useState('');
  const [trainerNotes, setTrainerNotes] = useState('');

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null); // { created, skipped, summary }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/users/clients`);
        const list = (res.data || []).filter((u) => u.role === 'client');
        if (!cancelled) setClients(list);
      } catch (e) {
        if (!cancelled) setError('No se pudo cargar la lista de clientes');
      } finally {
        if (!cancelled) setLoadingClients(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredClients = useMemo(() => {
    if (!search.trim()) return clients;
    const q = search.trim().toLowerCase();
    return clients.filter((c) =>
      (c.full_name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  }, [clients, search]);

  const toggleClient = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredClients.forEach((c) => next.add(c.id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const dateError = useMemo(() => {
    if (mode !== 'flexible') return '';
    if (availFrom && availUntil && availUntil < availFrom) {
      return 'La fecha "hasta" debe ser igual o posterior a "desde"';
    }
    return '';
  }, [mode, availFrom, availUntil]);

  const canSubmit = selectedIds.size > 0 && !dateError && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    // Safety: confirm large bulk operations to prevent accidental fan-out
    if (selectedIds.size >= 15) {
      const ok = window.confirm(
        `Vas a asignar esta plantilla a ${selectedIds.size} clientes. ¿Continuar?`
      );
      if (!ok) return;
    }
    setSubmitting(true);
    setError('');
    setSummary(null);
    try {
      const payload = {
        template_id: template.id,
        mode,
        client_ids: Array.from(selectedIds),
      };
      if (mode === 'session') {
        payload.weekday = weekday;
        payload.from_date = fromDate || null;
      } else {
        payload.available_from = availFrom || null;
        payload.available_until = availUntil || null;
        payload.trainer_notes = trainerNotes || null;
      }
      const res = await axios.post(`${API}/trainer/bulk-assign-template`, payload);
      setSummary(res.data);
      onCompleted?.(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error en la asignación');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- RENDER ----------
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/70 z-[9999] overflow-y-auto"
      onClick={onClose}
      data-testid="bulk-assign-modal"
    >
      <div className="min-h-screen flex items-start sm:items-center justify-center p-4">
        <div
          className="bg-blue-900/95 rounded-xl max-w-2xl w-full my-4 sm:my-8 max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 border-b border-blue-400/30 flex justify-between items-center bg-blue-950/40">
            <div>
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-300" />
                Asignar a varios clientes
              </h3>
              <p className="text-blue-300 text-sm mt-0.5">
                {template?.name || template?.template_name || 'Plantilla'}
              </p>
              <p className="text-amber-300 text-xs mt-1 flex items-center gap-1" data-testid="bulk-assign-est-duration">
                <Clock className="w-3 h-3" /> Duración estimada: ~{formatDuration(estimateWorkoutSeconds(template?.exercises || []))}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-blue-300 hover:text-white text-xl"
              data-testid="bulk-assign-close-btn"
            >
              ✕
            </button>
          </div>

          <div className="p-4 overflow-y-auto flex-1 space-y-5">
            {/* ============== RESULT VIEW ============== */}
            {summary ? (
              <div className="space-y-4">
                <div className="bg-green-500/10 border border-green-400/30 rounded-lg p-4 text-green-100">
                  <h4 className="font-bold text-lg mb-1">
                    ✅ {summary.summary?.created} asignación{summary.summary?.created !== 1 ? 'es' : ''} completada{summary.summary?.created !== 1 ? 's' : ''}
                  </h4>
                  {summary.created?.length > 0 && (
                    <ul className="text-sm space-y-0.5 mt-2 ml-4 list-disc">
                      {summary.created.map((c) => (
                        <li key={c.workout_id}>
                          <strong>{c.client_name}</strong> — {c.mode === 'flexible' ? '🏠 Rutina libre' : `📅 Sesión ${c.date}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {summary.skipped?.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-400/30 rounded-lg p-4 text-amber-100">
                    <h4 className="font-bold mb-1 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {summary.skipped.length} omitido{summary.skipped.length !== 1 ? 's' : ''}
                    </h4>
                    <ul className="text-sm space-y-0.5 mt-1 ml-4 list-disc">
                      {summary.skipped.map((s, i) => (
                        <li key={i}>
                          <strong>{s.client_name || s.client_id?.slice(0, 8)}</strong> —
                          {' '}{SKIP_REASON_LABEL[s.reason] || s.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* ============== STEP 1: CLIENTS ============== */}
                <section data-testid="bulk-step-clients">
                  <h4 className="text-blue-200 font-semibold mb-2 flex items-center gap-2">
                    <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">1</span>
                    Selecciona clientes
                    <span className="ml-auto text-sm font-normal text-blue-300">
                      {selectedIds.size} seleccionado{selectedIds.size !== 1 ? 's' : ''}
                    </span>
                  </h4>
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Buscar por nombre o email..."
                      className="input-field w-full pl-10"
                      data-testid="bulk-client-search"
                    />
                  </div>
                  <div className="flex gap-2 mb-2 text-xs">
                    <button
                      onClick={selectAllVisible}
                      className="text-blue-300 hover:text-blue-100 underline"
                      data-testid="bulk-select-all"
                    >
                      Seleccionar todos visibles
                    </button>
                    {selectedIds.size > 0 && (
                      <button
                        onClick={clearSelection}
                        className="text-blue-300 hover:text-blue-100 underline"
                      >
                        Limpiar selección
                      </button>
                    )}
                  </div>
                  {loadingClients ? (
                    <p className="text-blue-300 text-sm py-3">Cargando clientes...</p>
                  ) : (
                    <div className="rounded-lg bg-blue-950/40 max-h-52 overflow-y-auto p-1 space-y-0.5">
                      {filteredClients.length === 0 ? (
                        <p className="text-blue-300 text-sm py-2 px-2">Sin resultados</p>
                      ) : (
                        filteredClients.map((c) => (
                          <label
                            key={c.id}
                            className={`flex items-center gap-3 px-3 py-2 rounded cursor-pointer transition ${
                              selectedIds.has(c.id) ? 'bg-blue-500/30' : 'hover:bg-blue-800/30'
                            }`}
                            data-testid={`bulk-client-row-${c.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(c.id)}
                              onChange={() => toggleClient(c.id)}
                              className="w-4 h-4 accent-blue-400"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-white truncate">{c.full_name}</div>
                              <div className="text-xs text-blue-300 truncate">{c.email}</div>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </section>

                {/* ============== STEP 2: MODE ============== */}
                <section data-testid="bulk-step-mode">
                  <h4 className="text-blue-200 font-semibold mb-2 flex items-center gap-2">
                    <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">2</span>
                    Modo de asignación
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setMode('session')}
                      className={`p-3 rounded-lg border-2 text-left transition ${
                        mode === 'session'
                          ? 'border-blue-400 bg-blue-500/20 text-white'
                          : 'border-blue-400/20 bg-blue-950/40 text-blue-300 hover:border-blue-400/50'
                      }`}
                      data-testid="bulk-mode-session"
                    >
                      <div className="flex items-center gap-2 font-semibold">
                        <Calendar className="w-4 h-4" /> Sesión
                      </div>
                      <p className="text-xs mt-1 opacity-80">
                        Adjuntar a una cita existente del cliente
                      </p>
                    </button>
                    <button
                      onClick={() => setMode('flexible')}
                      className={`p-3 rounded-lg border-2 text-left transition ${
                        mode === 'flexible'
                          ? 'border-amber-400 bg-amber-500/20 text-white'
                          : 'border-blue-400/20 bg-blue-950/40 text-blue-300 hover:border-amber-400/50'
                      }`}
                      data-testid="bulk-mode-flexible"
                    >
                      <div className="flex items-center gap-2 font-semibold">
                        <Home className="w-4 h-4" /> Rutina libre
                      </div>
                      <p className="text-xs mt-1 opacity-80">
                        Sin cita — el cliente la hace cuando quiera
                      </p>
                    </button>
                  </div>
                </section>

                {/* ============== STEP 3: PARAMS ============== */}
                <section data-testid="bulk-step-params">
                  <h4 className="text-blue-200 font-semibold mb-2 flex items-center gap-2">
                    <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">3</span>
                    {mode === 'session' ? 'Día de la semana' : 'Disponibilidad y notas'}
                  </h4>

                  {mode === 'session' ? (
                    <div className="space-y-3 bg-blue-950/40 p-3 rounded-lg">
                      <div>
                        <label className="block text-blue-200 text-xs font-medium mb-1">
                          Día de la semana
                        </label>
                        <div className="grid grid-cols-7 gap-1">
                          {WEEKDAYS.map((d) => (
                            <button
                              key={d.value}
                              onClick={() => setWeekday(d.value)}
                              className={`py-2 rounded text-xs font-semibold transition ${
                                weekday === d.value
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-blue-800/30 text-blue-200 hover:bg-blue-700/50'
                              }`}
                              data-testid={`bulk-weekday-${d.value}`}
                            >
                              {d.label.slice(0, 3)}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-blue-200 text-xs font-medium mb-1">
                          Buscar próxima cita desde
                        </label>
                        <input
                          type="date"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                          className="input-field w-full text-sm"
                          data-testid="bulk-from-date"
                        />
                      </div>
                      <p className="text-xs text-blue-300/70 italic flex items-start gap-1.5">
                        <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        Para cada cliente seleccionado, el sistema buscará su próxima cita
                        confirmada en {WEEKDAYS[weekday]?.label.toLowerCase()} a partir de esa
                        fecha. Las citas que ya tengan rutina se omiten automáticamente.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 bg-amber-950/30 border border-amber-400/20 p-3 rounded-lg">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-amber-100 text-xs font-medium mb-1">
                            Disponible desde
                          </label>
                          <input
                            type="date"
                            value={availFrom}
                            onChange={(e) => setAvailFrom(e.target.value)}
                            className="input-field w-full text-sm"
                            data-testid="bulk-avail-from"
                          />
                        </div>
                        <div>
                          <label className="block text-amber-100 text-xs font-medium mb-1">
                            Hasta
                          </label>
                          <input
                            type="date"
                            value={availUntil}
                            onChange={(e) => setAvailUntil(e.target.value)}
                            className="input-field w-full text-sm"
                            data-testid="bulk-avail-until"
                          />
                        </div>
                      </div>
                      {dateError && (
                        <p className="text-red-300 text-xs">{dateError}</p>
                      )}
                      <div>
                        <label className="block text-amber-100 text-xs font-medium mb-1">
                          Notas para todos los clientes (opcional)
                        </label>
                        <textarea
                          value={trainerNotes}
                          onChange={(e) => setTrainerNotes(e.target.value)}
                          rows={2}
                          className="input-field w-full text-sm"
                          placeholder="Ej: Hazla esta semana, descansa 60-90s entre series..."
                          data-testid="bulk-notes"
                        />
                      </div>
                    </div>
                  )}
                </section>

                {error && (
                  <p className="text-red-300 text-sm bg-red-500/10 border border-red-400/30 rounded p-2">
                    {error}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-blue-400/30 flex gap-2">
            {summary ? (
              <button
                onClick={onClose}
                className="btn-primary flex-1"
                data-testid="bulk-assign-done-btn"
              >
                Cerrar
              </button>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="btn-secondary flex-1"
                  data-testid="bulk-assign-cancel-btn"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className={`flex-1 px-4 py-2 rounded-lg font-semibold transition ${
                    canSubmit
                      ? 'bg-blue-500 hover:bg-blue-400 text-white'
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                  data-testid="bulk-assign-submit-btn"
                >
                  {submitting
                    ? 'Asignando...'
                    : `Asignar a ${selectedIds.size} cliente${selectedIds.size !== 1 ? 's' : ''}`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BulkAssignTemplateModal;
